import {
  ScaffolderFormHeader,
  ScaffolderFormPaper,
  ScaffolderFieldError,
} from '@internal/plugin-scaffolder-common-field-react';
import React, { useCallback, useEffect, useRef } from 'react';

import { Box, Button } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';

import { KaapClusterPicker } from '../components/KaapClusterPicker';
import { SelfManagedClusterPicker } from '../components/SelfManagedClusterPicker';
import { useStyles } from '../styles';
import {
  ClusterConfiguration,
  KaapCluster,
  SelfManagedCluster,
} from '../types';
import { clusterInputErrors } from '../validation';

interface ClusterSectionProps {
  value: ClusterConfiguration;
  onChange: (value: ClusterConfiguration) => void;
  errors?: string[];
}
const MAX_CLUSTERS = 5;

const ClusterSectionInner = ({
  value,
  onChange,
  errors = [],
}: ClusterSectionProps) => {
  const classes = useStyles();
  const clusterError = errors.length > 0 ? clusterInputErrors(value) : '';
  const clusterRef = useRef<HTMLDivElement>(null);

  // Stable refs — keeps useCallback deps empty ([]) without stale closures.
  // Always read .current inside callbacks instead of closing over the prop directly.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => {
    if (clusterError) {
      clusterRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [clusterError]);

  useEffect(() => {
    if (!value) {
      onChangeRef.current({ kaapClusters: [], selfManagedClusters: [] });
    }
  }, [value]); // onChange intentionally excluded via ref

  const handleAddKaapCluster = useCallback(() => {
    const v = valueRef.current;
    const newKaapClusters = [
      ...(v?.kaapClusters || []),
      { tenant: null, namespaces: [] },
    ];
    onChangeRef.current({
      kaapClusters: newKaapClusters,
      selfManagedClusters: v?.selfManagedClusters || [],
    });
  }, []);

  const handleKaapClusterChange = useCallback(
    (index: number, cluster: KaapCluster) => {
      const v = valueRef.current;
      const newKaapClusters = [...(v?.kaapClusters || [])];
      newKaapClusters[index] = cluster;
      onChangeRef.current({
        kaapClusters: newKaapClusters,
        selfManagedClusters: v?.selfManagedClusters || [],
      });
    },
    [],
  );

  const handleRemoveKaapCluster = useCallback((index: number) => {
    const v = valueRef.current;
    const newKaapClusters = v?.kaapClusters.filter((_, i) => i !== index);
    onChangeRef.current({
      kaapClusters: newKaapClusters || [],
      selfManagedClusters: v?.selfManagedClusters || [],
    });
  }, []);

  const disableAddKaapCluster =
    value &&
    (value?.kaapClusters.length !== 0 ||
      value?.selfManagedClusters.length !== 0);

  const handleAddSelfManagedCluster = useCallback(() => {
    const v = valueRef.current;
    const newSelfManagedClusters = [
      ...(v?.selfManagedClusters || []),
      { cluster: null, namespaces: [] },
    ];
    onChangeRef.current({
      kaapClusters: v?.kaapClusters || [],
      selfManagedClusters: newSelfManagedClusters,
    });
  }, []);

  const handleSelfManagedClusterChange = useCallback(
    (index: number, cluster: SelfManagedCluster) => {
      const v = valueRef.current;
      const newSelfManagedClusters = [...(v?.selfManagedClusters || [])];
      newSelfManagedClusters[index] = cluster;
      onChangeRef.current({
        kaapClusters: v?.kaapClusters || [],
        selfManagedClusters: newSelfManagedClusters,
      });
    },
    [],
  );

  const handleRemoveSelfManagedCluster = useCallback((index: number) => {
    const v = valueRef.current;
    const newSelfManagedClusters = v?.selfManagedClusters.filter(
      (_, i) => i !== index,
    );
    onChangeRef.current({
      kaapClusters: v?.kaapClusters || [],
      selfManagedClusters: newSelfManagedClusters || [],
    });
  }, []);

  const disableAddSelfManagedCluster =
    value &&
    (value?.kaapClusters.length !== 0 ||
      value?.selfManagedClusters.length >= MAX_CLUSTERS);

  return (
    <Box>
      <ScaffolderFormPaper>
        <ScaffolderFormHeader
          title="Cluster Configuration"
          description="Configure the clusters for the project"
          divider
        />
        <div ref={clusterRef}>
          {(value?.kaapClusters || []).map((cluster, index) => (
            <Box key={cluster.tenant?.id ?? `kaap-${index}`}>
              <ScaffolderFormPaper>
                <ScaffolderFormHeader
                  title="Kaap Cluster"
                  variant="h6"
                  divider
                />
                <Box marginBottom={4}>
                  <KaapClusterPicker
                    formData={cluster}
                    onChange={e => handleKaapClusterChange(index, e)}
                    rawErrors={[]}
                  />
                </Box>
                <Button
                  variant="outlined"
                  color="secondary"
                  size="small"
                  onClick={() => handleRemoveKaapCluster(index)}
                  className={classes.removePaperButton}
                >
                  Remove
                </Button>
              </ScaffolderFormPaper>
            </Box>
          ))}
          {(value?.selfManagedClusters || []).map((cluster, index) => (
            <Box key={cluster.cluster?.id ?? `self-${index}`}>
              <ScaffolderFormPaper>
                <ScaffolderFormHeader
                  title="Self-Managed Cluster"
                  variant="h6"
                  divider
                />
                <Box marginBottom={4}>
                  <SelfManagedClusterPicker
                    formData={cluster}
                    onChange={e => handleSelfManagedClusterChange(index, e)}
                    rawErrors={[]}
                  />
                </Box>
                <Button
                  variant="outlined"
                  color="secondary"
                  size="small"
                  onClick={() => handleRemoveSelfManagedCluster(index)}
                  className={classes.removePaperButton}
                >
                  Remove
                </Button>
              </ScaffolderFormPaper>
            </Box>
          ))}
          {clusterError && (
            <Box className={classes.inputContainer}>
              <ScaffolderFieldError message={clusterError} />
            </Box>
          )}
          <Box className={classes.addButtonContainer}>
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddKaapCluster}
              disabled={disableAddKaapCluster}
            >
              Add Kaap Cluster
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddSelfManagedCluster}
              disabled={disableAddSelfManagedCluster}
            >
              Add Self-Managed Cluster
            </Button>
          </Box>
        </div>
      </ScaffolderFormPaper>
    </Box>
  );
};

export const ClusterSection = React.memo(ClusterSectionInner);
